/** Shared UI kit (docs/11-ux-ui.md). Screens compose these; styling lives in styles/. */
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { getTier } from '@fakerpg/core';
import {
  AlertTriangle,
  ArrowUpCircle,
  CheckCircle2,
  Coins,
  Gift,
  Info,
  Sparkles,
  Swords,
  Trophy,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Sprite } from '../assets/Sprite.js';
import { ELEMENT_ICONS } from '../assets/art.js';
import { elementColor, tierStyle } from '../assets/palette.js';
import type { UiIcon } from '../assets/keys.js';
import { useLang, useNow, useSnapshot, useStore, useT } from '../game/context.js';
import type { ToastIcon } from '../game/store.js';
import { formatClock, formatCompact, formatNumber } from '../i18n/index.js';
import { CountUp } from './effects.js';
import { Portal } from './Portal.js';
import { play } from './sfx.js';

// ── Buttons ─────────────────────────────────────────────────────

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'magic';

export function Btn({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  block = false,
  children,
  className = '',
  onClick,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  block?: boolean;
}) {
  return (
    <button
      type="button"
      className={`btn btn--${variant} btn--${size} ${block ? 'btn--block' : ''} ${className}`}
      onClick={(e) => {
        play('tap');
        onClick?.(e);
      }}
      {...rest}
    >
      {Icon && <Icon className="btn__icon" size={size === 'sm' ? 16 : 18} strokeWidth={2.2} aria-hidden="true" />}
      {children !== undefined && <span className="btn__label">{children}</span>}
      {IconRight && <IconRight className="btn__icon" size={size === 'sm' ? 16 : 18} strokeWidth={2.2} aria-hidden="true" />}
    </button>
  );
}

// ── Progress ────────────────────────────────────────────────────

export function Bar({ value, max, tone = 'gold', label, size = 'md', showGhost }: { value: number; max: number; tone?: 'gold' | 'hp' | 'xp' | 'energy' | 'enemy' | 'magic'; label?: string; size?: 'sm' | 'md' | 'lg'; showGhost?: number }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const ghost = showGhost !== undefined && max > 0 ? Math.max(0, Math.min(100, (showGhost / max) * 100)) : null;
  return (
    <div className={`bar bar--${tone} bar--${size}`} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.max(0, Math.round(value))} aria-label={label}>
      {ghost !== null && <div className="bar__ghost" style={{ width: `${ghost}%` }} />}
      <div className="bar__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Ring({ value, max, size = 64, stroke = 6, color = 'var(--gold)', track = 'var(--track)', children, label }: { value: number; max: number; size?: number; stroke?: number; color?: string; track?: string; children?: ReactNode; label?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <span className="ring" style={{ width: size, height: size }} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.round(value)} aria-label={label}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          className="ring__fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children !== undefined && <span className="ring__center">{children}</span>}
    </span>
  );
}

// ── Surfaces ────────────────────────────────────────────────────

export function Panel({ title, icon: Icon, actions, children, className = '', tone }: { title?: ReactNode; icon?: LucideIcon; actions?: ReactNode; children?: ReactNode; className?: string; tone?: 'gold' | 'magic' | 'success' | 'danger' }) {
  return (
    <section className={`panel ${tone ? `panel--${tone}` : ''} ${className}`}>
      {(title || actions) && (
        <header className="panel__head">
          {title && (
            <h2 className="panel__title">
              {Icon && <Icon size={18} strokeWidth={2.2} aria-hidden="true" />}
              {title}
            </h2>
          )}
          {actions && <div className="panel__actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHead({ title, subtitle, icon: Icon, actions }: { title: string; subtitle?: ReactNode; icon?: LucideIcon; actions?: ReactNode }) {
  return (
    <header className="pagehead">
      <div className="pagehead__text">
        <h1 className="pagehead__title">
          {Icon && <Icon className="pagehead__icon" size={26} strokeWidth={2} aria-hidden="true" />}
          {title}
        </h1>
        {subtitle && <p className="pagehead__sub">{subtitle}</p>}
      </div>
      {actions && <div className="pagehead__actions">{actions}</div>}
    </header>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label, size = 'md' }: { value: T; options: { value: T; label: ReactNode; disabled?: boolean; title?: string; badge?: ReactNode }[]; onChange: (v: T) => void; label: string; size?: 'sm' | 'md' }) {
  return (
    <div className={`segmented segmented--${size}`} role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={o.value === value}
          className={`segmented__item ${o.value === value ? 'is-active' : ''}`}
          disabled={o.disabled}
          title={o.title}
          onClick={() => {
            play('tap');
            onChange(o.value);
          }}
        >
          {o.label}
          {o.badge}
        </button>
      ))}
    </div>
  );
}

/** Bottom sheet on phones, centered dialog on wide screens. */
export function Sheet({ title, onClose, children, footer, wide = false }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose]);
  return (
    <Portal>
    <div className="overlay" role="presentation" onClick={onClose}>
      <div className={`sheet ${wide ? 'sheet--wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <span className="sheet__grip" aria-hidden="true" />
        <header className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          <button type="button" className="iconbtn" onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </button>
        </header>
        <div className="sheet__body">{children}</div>
        {footer && <footer className="sheet__foot">{footer}</footer>}
      </div>
    </div>
    </Portal>
  );
}

/** Small anchored panel; closes on outside click or Escape. */
export function Popover({ trigger, children, label, align = 'end' }: { trigger: (open: boolean, toggle: () => void) => ReactNode; children: ReactNode; label: string; align?: 'start' | 'end' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span className="popover" ref={ref}>
      {trigger(open, () => setOpen((o) => !o))}
      {open && (
        <span className={`popover__panel popover__panel--${align}`} role="dialog" aria-label={label}>
          {children}
        </span>
      )}
    </span>
  );
}

export function Switch({ checked, onChange, label, description, disabled = false }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  return (
    <label className={`switchrow ${disabled ? 'is-disabled' : ''}`}>
      <span className="switchrow__text">
        <span className="switchrow__label">{label}</span>
        {description && <span className="switchrow__desc">{description}</span>}
      </span>
      <input type="checkbox" role="switch" className="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

// ── Small pieces ────────────────────────────────────────────────

export function Badge({ count, dot = false, tone = 'alert' }: { count?: number; dot?: boolean; tone?: 'alert' | 'gold' }) {
  if (dot) return <span className={`badge-dot badge-dot--${tone}`} aria-hidden="true" />;
  if (!count) return null;
  return <span className={`badge-count badge-count--${tone}`}>{count > 99 ? '99+' : count}</span>;
}

export function Tag({ children, tone = 'neutral', icon: Icon }: { children: ReactNode; tone?: 'neutral' | 'gold' | 'magic' | 'success' | 'danger' | 'new'; icon?: LucideIcon }) {
  return (
    <span className={`tag tag--${tone}`}>
      {Icon && <Icon size={13} strokeWidth={2.4} aria-hidden="true" />}
      {children}
    </span>
  );
}

export function TierTag({ tier }: { tier: number }) {
  const lang = useLang();
  const data = getTier(tier);
  if (!data) return null;
  return (
    <span className="tag tag--tier" style={{ ['--tier' as string]: tierStyle(tier).base }}>
      <TierGems tier={tier} />
      {data.name[lang]}
    </span>
  );
}

export function TierGems({ tier }: { tier: number }) {
  return (
    <span className="gems" aria-hidden="true">
      {Array.from({ length: tier }, (_, i) => (
        <i key={i} />
      ))}
    </span>
  );
}

export function ElementTag({ element, compact = false }: { element: string; compact?: boolean }) {
  const t = useT();
  const Icon = ELEMENT_ICONS[element];
  const known = element in ELEMENT_ICONS;
  const name = known ? t(`elements.${element as 'Neutral'}`) : element || '—';
  return (
    <span className={`tag tag--element ${compact ? 'tag--compact' : ''}`} style={{ ['--elem' as string]: elementColor(element) }} title={name}>
      {Icon && <Icon size={13} strokeWidth={2.4} aria-hidden="true" />}
      {compact ? <span className="sr-only">{name}</span> : name}
    </span>
  );
}

export function Amount({ icon, value, compact = false, title, animate = false, size = 18 }: { icon: UiIcon; value: number; compact?: boolean; title?: string; animate?: boolean; size?: number }) {
  const lang = useLang();
  const fmt = (n: number) => (compact ? formatCompact(lang, n) : formatNumber(lang, n));
  return (
    <span className="amount" title={title}>
      <Sprite asset={{ kind: 'ui', name: icon }} size={size} title={title ?? icon} decorative={!title} />
      <span className="amount__value">{animate ? <CountUp value={value} format={fmt} /> : fmt(value)}</span>
    </span>
  );
}

export function Countdown({ at, compact = false }: { at: number; compact?: boolean }) {
  const now = useNow();
  return <span className={`countdown ${compact ? 'countdown--compact' : ''}`}>{formatClock(at - now)}</span>;
}

export function Empty({ icon: Icon = Info, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <div className="empty">
      <Icon size={28} strokeWidth={1.8} aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

// ── Toasts ──────────────────────────────────────────────────────

const TOAST_ICONS: Record<ToastIcon, LucideIcon> = {
  level: ArrowUpCircle,
  coin: Coins,
  loot: Sparkles,
  gift: Gift,
  sword: Swords,
  trophy: Trophy,
  energy: Zap,
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

export function Toasts() {
  const { toasts } = useSnapshot();
  const store = useStore();
  const oldest = toasts[0];
  const newest = toasts[toasts.length - 1];
  useEffect(() => {
    if (!oldest) return;
    const id = setTimeout(() => store.dismissToast(oldest.id), oldest.tone === 'error' ? 4500 : 3200);
    return () => clearTimeout(id);
  }, [oldest, store]);
  useEffect(() => {
    if (newest?.tone === 'error') play('error');
  }, [newest]);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((toast) => {
        const Icon = TOAST_ICONS[toast.icon ?? (toast.tone === 'error' ? 'error' : 'success')];
        return (
          <button key={toast.id} type="button" className={`toast toast--${toast.tone}`} onClick={() => store.dismissToast(toast.id)}>
            <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
            <span>{toast.text}</span>
          </button>
        );
      })}
    </div>
  );
}
